import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Eye, Clock, Calendar, Award } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const AdvancedAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [analyticsData, setAnalyticsData] = useState({
    pageViews: 12543,
    uniqueVisitors: 3421,
    avgSessionDuration: '3:24',
    bounceRate: '42%',
    topPages: [
      { page: '/tarifs', views: 3210, percentage: 25 },
      { page: '/', views: 2890, percentage: 23 },
      { page: '/portfolio', views: 2103, percentage: 17 },
      { page: '/blog', views: 1567, percentage: 12 },
      { page: '/avis', views: 987, percentage: 8 }
    ],
    conversionMetrics: {
      whatsappClicks: 487,
      formSubmissions: 0,
      reviewsSubmitted: 8
    },
    trafficSources: [
      { source: 'Direct', visitors: 1245, percentage: 36 },
      { source: 'Google Search', visitors: 982, percentage: 29 },
      { source: 'Facebook', visitors: 654, percentage: 19 },
      { source: 'Instagram', visitors: 340, percentage: 10 },
      { source: 'Autres', visitors: 200, percentage: 6 }
    ]
  });

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Statistiques Avancées</h2>
        <p className="text-gray-600 mt-1">Analysez les performances de votre site web</p>
      </div>

      {/* Time Range Selector */}
      <div className="mb-6 flex gap-3">
        {['7days', '30days', '90days', 'all'].map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              timeRange === range
                ? 'bg-amber-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {range === '7days' && '7 jours'}
            {range === '30days' && '30 jours'}
            {range === '90days' && '90 jours'}
            {range === 'all' && 'Tout'}
          </button>
        ))}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Vues de Pages</p>
                <p className="text-3xl font-bold mt-2">{analyticsData.pageViews.toLocaleString()}</p>
              </div>
              <Eye className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Visiteurs Uniques</p>
                <p className="text-3xl font-bold mt-2">{analyticsData.uniqueVisitors.toLocaleString()}</p>
              </div>
              <Users className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Durée Moyenne</p>
                <p className="text-3xl font-bold mt-2">{analyticsData.avgSessionDuration}</p>
              </div>
              <Clock className="h-10 w-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">Taux de Rebond</p>
                <p className="text-3xl font-bold mt-2">{analyticsData.bounceRate}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Pages */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Pages les Plus Visitées</h3>
          <div className="space-y-4">
            {analyticsData.topPages.map((page, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{page.page}</span>
                    <span className="text-gray-600">{page.views.toLocaleString()} vues</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-amber-500 to-amber-600 h-3 rounded-full transition-all"
                      style={{ width: `${page.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">🌐 Sources de Trafic</h3>
            <div className="space-y-3">
              {analyticsData.trafficSources.map((source, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">{source.source}</span>
                  <div className="text-right">
                    <span className="font-bold text-gray-900">{source.visitors.toLocaleString()}</span>
                    <span className="text-sm text-gray-500 ml-2">({source.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Conversion Metrics */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">🎯 Métriques de Conversion</h3>
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Clics WhatsApp</span>
                  <span className="text-2xl font-bold text-green-600">{analyticsData.conversionMetrics.whatsappClicks}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Principal canal de conversion</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Avis Soumis</span>
                  <span className="text-2xl font-bold text-blue-600">{analyticsData.conversionMetrics.reviewsSubmitted}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Nouveaux témoignages</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <h3 className="font-bold text-blue-900 mb-2">📊 Intégration Google Analytics</h3>
          <p className="text-sm text-blue-800">
            Ces statistiques sont actuellement simulées. Pour afficher vos données réelles, 
            configurez votre ID Google Analytics 4 dans les Paramètres du Site.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedAnalytics;
